<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    class="group relative z-0 inline-flex items-center justify-center font-bold"
    :class="[
      {
        'px-2.5 py-1.5 text-xs': size === 'xs',
        'px-3 py-2 text-sm': size === 'sm',
        'h-10 pr-4 pl-6 text-md': size === 'md',
        'h-10 pr-4 pl-6 text-base': size === 'lg',
        'px-6 py-2 text-base': size === 'xl',
        'px-14 py-3 text-base uppercase lg:px-10 lg:py-4 lg:text-xl':
          size === 'home',
      },
      buttonLink === false ? baseClasses : '',
      finalBackgroundColor,
      finalBorderColor,
      finalHoverColor,
      finalColor,
      finalRingColor,
      disabled || loading ? 'cursor-not-allowed opacity-50' : '',
    ]"
    :style="[buttonLink ? { padding: '0px' } : '']"
  >
    <span
      class="z-10 leading-4 whitespace-nowrap"
      :class="{
        'pr-2': !icon && !svg,
        'hidden md:inline': mobileIcon,
      }"
      ><slot></slot
    ></span>
    <img
      v-if="svg"
      :src="getSvgUrl(svg)"
      class="max-w-none"
      :class="{
        'ml-4': !!$slots.default && !iconOnly && !mobileIcon,
        'md:ml-4': !!$slots.default && !iconOnly && mobileIcon,
      }"
      alt=""
    />
    <i
      v-if="icon"
      :class="[
        iconType + ' fa-' + icon,
        { hidden: iconOnly && loading },
        { 'ml-4': !!$slots.default && !iconOnly && !mobileIcon },
        { '-ml-2': iconOnly || mobileIcon },
        { 'inline md:hidden': mobileIcon },
      ]"
    />
    <span v-if="mobileIcon" class="md:ml-2"></span>
    <i
      v-if="loading"
      class="fas fa-circle-notch fa-spin"
      :class="[iconOnly ? '-ml-2' : 'ml-3']"
    />
    <span
      v-if="underlineEffect || underline"
      class="absolute left-0 z-0 hidden h-0.5 bg-primary-500 lg:bottom-1 lg:block lg:h-1"
      :class="[
        underline
          ? 'w-full'
          : 'w-0 group-hover:w-full group-hover:transition-all group-hover:duration-500',
      ]"
    ></span>
  </button>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue';
import { ButtonVariant } from '@/model/button';

const props = defineProps({
  size: {
    type: String as PropType<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'home'>,
    default: 'md',
  },
  textColor: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  borderColor: {
    type: String,
    default: '',
  },
  hoverColor: {
    type: String,
    default: '',
  },
  activeColor: {
    type: String,
    default: '',
  },
  ringColor: {
    type: String,
    default: '',
  },
  svg: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  iconType: {
    type: String,
    default: 'far',
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  right: {
    type: Boolean,
    default: false,
  },
  buttonLink: {
    type: Boolean,
    default: false,
  },
  baseClasses: {
    type: String,
    default: 'rounded-lg',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String as PropType<'button' | 'submit'>,
    default: 'button',
  },
  underlineEffect: {
    type: Boolean,
    default: false,
  },
  underline: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String as PropType<ButtonVariant | string>,
    default: ButtonVariant.PRIMARY,
  },
  mobileIcon: {
    type: Boolean,
    default: false,
  },
});
const variants = {
  primary: {
    backgroundColor: 'bg-primary-600',
    color: 'text-primary-50',
    buttonLinkColor: 'text-primary-500',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-primary-400',
    buttonLinkHoverColor: 'hover:text-primary-700',
    ringColor: 'ring-primary-500',
  },
  soft: {
    backgroundColor: 'bg-primary-100',
    color: 'text-primary-700',
    buttonLinkColor: 'text-primary-300',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-primary-50',
    buttonLinkHoverColor: 'hover:text-primary-500',
    ringColor: 'ring-primary-500',
  },
  outlined: {
    backgroundColor: 'bg-transparent',
    color: 'text-primary-500',
    borderColor: 'border-primary-200',
    hoverColor: 'hover:bg-primary-100',
    ringColor: 'ring-primary-500',
  },
  text: {
    backgroundColor: 'bg-transparent',
    color: 'text-primary-500',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-primary-100',
    ringColor: 'ring-primary-500',
  },
  dark: {
    backgroundColor: 'bg-gray-800',
    color: 'text-white',
    buttonLinkColor: 'text-gray-900',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-gray-900',
    buttonLinkHoverColor: 'hover:text-gray-700',
    ringColor: 'ring-gray-800',
  },
  'home-dark': {
    backgroundColor: 'bg-home-gray-800',
    color: 'text-white',
    buttonLinkColor: 'text-gray-800',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-home-gray-900',
    buttonLinkHoverColor: 'hover:text-gray-900',
    ringColor: 'ring-home-gray-700',
  },
  'home-yellow': {
    backgroundColor: 'bg-home-yellow',
    color: 'text-dark',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-home-yellow/90',
    ringColor: 'ring-home-yellow',
  },
  medium: {
    backgroundColor: 'bg-medium',
    color: 'text-white',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-gray-700',
    ringColor: 'ring-gray-900',
  },
  secondary: {
    backgroundColor: 'bg-secondary',
    color: 'text-dark',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-gray-300',
    ringColor: 'ring-gray-500',
  },
  danger: {
    backgroundColor: 'bg-red-600',
    color: 'text-red-50',
    borderColor: 'border-transparent',
    hoverColor: 'hover:bg-red-700',
    ringColor: 'ring-red-600',
  },
};

const finalBackgroundColor = computed(() => {
  if (props.color !== '') {
    return props.color;
  }
  if (props.buttonLink) {
    return '';
  }
  if (props.variant !== '') {
    if (variants[props.variant].backgroundColor !== undefined) {
      return variants[props.variant].backgroundColor;
    }
  }
  return variants['primary'].backgroundColor;
});

const finalColor = computed(() => {
  if (props.textColor !== '') {
    return props.textColor;
  }
  if (props.buttonLink) {
    if (variants[props.variant].buttonLinkColor) {
      return variants[props.variant].buttonLinkColor;
    } else {
      return variants['primary'].color;
    }
  }
  if (props.variant !== '') {
    if (variants[props.variant].color !== '') {
      return variants[props.variant].color;
    }
  }
  return variants['primary'].color;
});
const finalBorderColor = computed(() => {
  if (props.borderColor !== '') {
    return props.borderColor;
  }
  if (props.buttonLink) {
    return '';
  }

  const borderClasses = ' border-2 hover:border-transparent';
  if (props.variant !== '') {
    if (variants[props.variant].borderColor !== '') {
      return variants[props.variant].borderColor + borderClasses;
    }
  }
  return variants['primary'].borderColor + borderClasses;
});

const finalHoverColor = computed(() => {
  if (props.hoverColor !== '') {
    return props.hoverColor;
  }
  if (props.buttonLink) {
    if (variants[props.variant].buttonLinkHoverColor) {
      return variants[props.variant].buttonLinkHoverColor;
    } else {
      return variants['primary'].buttonLinkHoverColor;
    }
  }
  if (props.variant !== '') {
    if (variants[props.variant].hoverColor !== '') {
      return variants[props.variant].hoverColor;
    }
  }
  return variants['primary'].hoverColor;
});

const finalRingColor = computed(() => {
  if (props.ringColor !== '') {
    return props.ringColor;
  }
  if (props.buttonLink) {
    return '';
  }
  const ringClasses = ' active:ring-2 active:ring-offset-2';
  if (props.variant !== '') {
    if (variants[props.variant].ringColor !== '') {
      return variants[props.variant].ringColor + ringClasses;
    }
  }
  return variants['primary'].ringColor + ringClasses;
});

function getSvgUrl(name) {
  return new URL(`/src/assets/img/${name}.svg`, import.meta.url).href;
}
</script>
